import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import { AlertCircle, FileText, LoaderCircle, Menu, Mic, Paperclip, RefreshCw, SendHorizontal, Square, Trash2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { api, streamRequest } from "../api";
import type { ChatDetail, Message } from "../types";

type Props = {
  chatId: number | null;
  onOpenSidebar: () => void;
  onChatsChanged: () => void;
  onSettings: () => void;
  onToast: (message: string, kind?: "success" | "error") => void;
};

type AgentStatus = "thinking" | "writing" | "memory_saved" | null;
type ReplyError = { code: string; message: string; retryable: boolean; userMessageId?: number } | null;
type SpeechResult = { isFinal: boolean; 0: { transcript: string } };
type SpeechResultEvent = { resultIndex: number; results: ArrayLike<SpeechResult> };
type SpeechErrorEvent = { error: string };
type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onresult: ((event: SpeechResultEvent) => void) | null;
  onerror: ((event: SpeechErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
};
type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

const statusLabels: Record<Exclude<AgentStatus, null>, string> = {
  thinking: "думает",
  writing: "пишет",
  memory_saved: "сохранил память",
};

function formatBytes(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

function MessageContent({ message }: { message: Message }) {
  return (
    <article className={`message ${message.role}`}>
      {message.role === "assistant" && <span className="assistant-avatar" aria-hidden="true">S</span>}
      <div className="message-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
        {message.status === "interrupted" && (
          <p className="interrupted-label"><AlertCircle /> ответ оборвался</p>
        )}
      </div>
    </article>
  );
}

export function ChatView({ chatId, onOpenSidebar, onChatsChanged, onSettings, onToast }: Props) {
  const [chat, setChat] = useState<ChatDetail | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [streamedText, setStreamedText] = useState("");
  const [agentStatus, setAgentStatus] = useState<AgentStatus>(null);
  const [replyError, setReplyError] = useState<ReplyError>(null);
  const [uploading, setUploading] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recorderStreamRef = useRef<MediaStream | null>(null);
  const recorderChunksRef = useRef<Blob[]>([]);
  const discardRecordingRef = useRef(false);
  const recordingTimeoutRef = useRef<number | null>(null);
  const voiceSessionRef = useRef(0);
  const speechDraftRef = useRef("");
  const speechFinalRef = useRef("");

  const stopRecorderTracks = () => {
    recorderStreamRef.current?.getTracks().forEach((track) => track.stop());
    recorderStreamRef.current = null;
  };

  const cancelVoiceInput = () => {
    voiceSessionRef.current += 1;
    recognitionRef.current?.abort();
    recognitionRef.current = null;
    if (recordingTimeoutRef.current !== null) window.clearTimeout(recordingTimeoutRef.current);
    recordingTimeoutRef.current = null;
    discardRecordingRef.current = true;
    if (recorderRef.current?.state !== "inactive") recorderRef.current?.stop();
    stopRecorderTracks();
    setListening(false);
  };

  const loadChat = async () => {
    if (!chatId) { setChat(null); return null; }
    const detail = await api.chat(chatId);
    setChat(detail);
    return detail;
  };

  useEffect(() => {
    cancelVoiceInput();
    setTranscribing(false);
    setReplyError(null);
    setStreamedText("");
    void loadChat().catch((error) => onToast(error.message, "error"));
  }, [chatId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => cancelVoiceInput(), []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: busy ? "smooth" : "auto" });
  }, [chat?.messages.length, streamedText, agentStatus, busy]);

  const runStream = async (path: string, body?: Record<string, unknown>) => {
    if (!chatId || busy) return;
    setBusy(true);
    setReplyError(null);
    setStreamedText("");
    let receivedError: ReplyError = null;
    try {
      await streamRequest(path, body, ({ type, data }) => {
        if (type === "status") setAgentStatus(data.status as AgentStatus);
        if (type === "delta") setStreamedText((current) => current + String(data.text ?? ""));
        if (type === "memory_saved") {
          setAgentStatus("memory_saved");
          onToast("Новый факт сохранён в памяти");
        }
        if (type === "error") {
          receivedError = {
            code: String(data.code ?? "error"),
            message: String(data.message ?? "Не удалось получить ответ"),
            retryable: Boolean(data.retryable),
          };
          if (data.redirectToSettings) onSettings();
        }
      });
    } catch (caught) {
      receivedError = { code: "network_error", message: caught instanceof Error ? caught.message : "Соединение оборвалось", retryable: true };
    } finally {
      const updated = await loadChat().catch(() => null);
      if (receivedError && updated) {
        const lastUser = [...updated.messages].reverse().find((message) => message.role === "user");
        receivedError.userMessageId = lastUser?.id;
      }
      setReplyError(receivedError);
      setStreamedText("");
      setAgentStatus(null);
      setBusy(false);
      onChatsChanged();
    }
  };

  const send = async () => {
    const content = draft.trim();
    if (!content || !chatId || busy) return;
    cancelVoiceInput();
    setDraft("");
    setChat((current) => current ? {
      ...current,
      messages: [...current.messages, { id: -Date.now(), chat_id: chatId, role: "user", content, status: "complete", created_at: new Date().toISOString() }],
    } : current);
    await runStream(`/chats/${chatId}/messages`, { content });
  };

  const keyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void send();
    }
  };

  const startBrowserRecognition = () => {
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }

    const speechWindow = window as typeof window & {
      SpeechRecognition?: SpeechRecognitionConstructor;
      webkitSpeechRecognition?: SpeechRecognitionConstructor;
    };
    const Recognition = speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
    if (!Recognition) {
      onToast("Голосовой ввод не поддерживается этим браузером", "error");
      return;
    }

    const recognition = new Recognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "ru-RU";
    recognition.maxAlternatives = 1;
    speechDraftRef.current = draft.trimEnd();
    speechFinalRef.current = "";

    recognition.onresult = (event) => {
      const interim: string[] = [];
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const transcript = event.results[index][0].transcript.trim();
        if (!transcript) continue;
        if (event.results[index].isFinal) {
          speechFinalRef.current = [speechFinalRef.current, transcript].filter(Boolean).join(" ");
        } else {
          interim.push(transcript);
        }
      }
      const spoken = [speechFinalRef.current, interim.join(" ")].filter(Boolean).join(" ");
      setDraft([speechDraftRef.current, spoken].filter(Boolean).join(" "));
    };
    recognition.onerror = (event) => {
      setListening(false);
      recognitionRef.current = null;
      if (event.error === "aborted") return;
      const message = event.error === "not-allowed" || event.error === "service-not-allowed"
        ? "Разрешите браузеру доступ к микрофону"
        : event.error === "no-speech"
          ? "Речь не распознана"
          : "Не удалось включить голосовой ввод";
      onToast(message, "error");
    };
    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
    };

    try {
      recognitionRef.current = recognition;
      recognition.start();
      setListening(true);
    } catch {
      recognitionRef.current = null;
      setListening(false);
      onToast("Не удалось включить голосовой ввод", "error");
    }
  };

  const toggleVoiceInput = async () => {
    if (transcribing) return;
    if (listening) {
      if (recorderRef.current?.state !== "inactive") recorderRef.current?.stop();
      else recognitionRef.current?.stop();
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      startBrowserRecognition();
      return;
    }

    try {
      const voiceSession = voiceSessionRef.current;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (voiceSession !== voiceSessionRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const preferredTypes = ["audio/webm;codecs=opus", "audio/mp4", "audio/webm", "audio/ogg;codecs=opus"];
      const mimeType = preferredTypes.find((type) => MediaRecorder.isTypeSupported(type));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;
      recorderStreamRef.current = stream;
      recorderChunksRef.current = [];
      discardRecordingRef.current = false;

      recorder.ondataavailable = (event) => {
        if (event.data.size) recorderChunksRef.current.push(event.data);
      };
      recorder.onerror = () => {
        stopRecorderTracks();
        setListening(false);
        onToast("Не удалось записать голос", "error");
      };
      recorder.onstop = async () => {
        if (recordingTimeoutRef.current !== null) window.clearTimeout(recordingTimeoutRef.current);
        recordingTimeoutRef.current = null;
        setListening(false);
        stopRecorderTracks();
        const chunks = recorderChunksRef.current;
        recorderChunksRef.current = [];
        recorderRef.current = null;
        if (discardRecordingRef.current || !chunks.length) return;

        const type = recorder.mimeType || chunks[0].type || "audio/webm";
        const extension = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        setTranscribing(true);
        try {
          const result = await api.transcribeVoice(new Blob(chunks, { type }), `voice.${extension}`);
          if (voiceSession === voiceSessionRef.current) {
            setDraft((current) => [current.trimEnd(), result.text].filter(Boolean).join(" "));
            onToast("Распознанный текст добавлен в сообщение");
          }
        } catch (caught) {
          onToast(caught instanceof Error ? caught.message : "Не удалось распознать речь", "error");
        } finally {
          setTranscribing(false);
        }
      };

      recorder.start(250);
      setListening(true);
      recordingTimeoutRef.current = window.setTimeout(() => {
        if (recorder.state !== "inactive") recorder.stop();
      }, 60_000);
    } catch (caught) {
      stopRecorderTracks();
      setListening(false);
      const denied = caught instanceof DOMException && ["NotAllowedError", "SecurityError"].includes(caught.name);
      onToast(denied ? "Разрешите браузеру доступ к микрофону" : "Не удалось включить микрофон", "error");
    }
  };

  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!chatId || !event.target.files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(event.target.files)) await api.uploadFile(chatId, file);
      await loadChat();
      onToast("Файл прикреплён");
    } catch (caught) {
      onToast(caught instanceof Error ? caught.message : "Не удалось прикрепить файл", "error");
    } finally {
      event.target.value = "";
      setUploading(false);
    }
  };

  const removeFile = async (fileId: number) => {
    if (!chatId) return;
    try {
      await api.deleteFile(chatId, fileId);
      await loadChat();
    } catch (caught) {
      onToast(caught instanceof Error ? caught.message : "Не удалось удалить файл", "error");
    }
  };

  if (!chatId || !chat) {
    return (
      <section className="chat-page empty-chat">
        <header className="mobile-topbar"><button className="icon-button button-ghost" aria-label="Открыть список чатов" onClick={onOpenSidebar}><Menu /></button><strong>SuperGPT</strong></header>
        <div className="empty-state"><span className="brand-mark large">S</span><h1>Начните новый разговор</h1></div>
      </section>
    );
  }

  const lastAssistant = [...chat.messages].reverse().find((message) => message.role === "assistant");
  const lastInterrupted = lastAssistant?.status === "interrupted" ? lastAssistant : undefined;
  const userBeforeInterrupted = lastInterrupted ? [...chat.messages].reverse().find((message) => message.role === "user" && message.id < lastInterrupted.id) : undefined;

  return (
    <section className="chat-page">
      <header className="chat-topbar">
        <button className="icon-button button-ghost mobile-only" type="button" aria-label="Открыть список чатов" title="Чаты" onClick={onOpenSidebar}><Menu /></button>
        <div className="chat-title"><h1>{chat.title}</h1><small>DeepSeek V4 Pro</small></div>
      </header>

      <div className="messages-scroll" aria-live="polite">
        <div className="messages-column">
          {!chat.messages.length && (
            <div className="empty-state chat-welcome"><span className="brand-mark large">S</span><h2>Чем займёмся?</h2></div>
          )}
          {chat.messages.map((message) => <MessageContent key={message.id} message={message} />)}
          {streamedText && <MessageContent message={{ id: -1, chat_id: chat.id, role: "assistant", content: streamedText, status: "streaming", created_at: "" }} />}
          {agentStatus && (
            <div className={`agent-state ${agentStatus}`}><span className="state-dot" />{statusLabels[agentStatus]}</div>
          )}
          {replyError && (
            <div className="reply-error" role="alert">
              <AlertCircle />
              <span>{replyError.message}</span>
              {replyError.retryable && replyError.userMessageId && (
                <button className="button button-ghost" type="button" onClick={() => void runStream(`/chats/${chat.id}/messages/${replyError.userMessageId}/retry`)}><RefreshCw /> Повторить</button>
              )}
            </div>
          )}
          {lastInterrupted && !busy && (
            <div className="message-actions">
              {userBeforeInterrupted && <button className="button button-ghost" type="button" onClick={() => void runStream(`/chats/${chat.id}/messages/${userBeforeInterrupted.id}/retry`)}><RefreshCw /> Повторить</button>}
              <button className="button button-glass" type="button" onClick={() => void runStream(`/chats/${chat.id}/messages/${lastInterrupted.id}/continue`)}><SendHorizontal /> Продолжить ответ</button>
            </div>
          )}
          <div ref={bottom} />
        </div>
      </div>

      <footer className="composer-area">
        {!!chat.files.length && (
          <div className="attached-files" aria-label="Прикреплённые файлы">
            {chat.files.map((file) => (
              <div className={`file-chip ${file.status}`} key={file.id} title={file.original_name}>
                <FileText />
                <span><strong>{file.original_name}</strong><small>{file.status === "failed" ? file.error_message : formatBytes(file.size_bytes)}</small></span>
                <button className="icon-plain" type="button" aria-label={`Удалить ${file.original_name}`} title="Удалить файл" onClick={() => void removeFile(file.id)}><Trash2 /></button>
              </div>
            ))}
          </div>
        )}
        {(listening || transcribing) && (
          <div className={`voice-status${listening ? " recording" : ""}`} role="status">
            <span className="voice-status-dot" aria-hidden="true" />
            {listening ? "Идёт запись. Нажмите красную кнопку, чтобы закончить" : "Распознаю речь…"}
          </div>
        )}
        <div className="composer">
          <input ref={fileInput} hidden type="file" multiple accept=".txt,.md,.pdf,.docx" onChange={(event) => void upload(event)} />
          <button className="icon-button button-ghost attach-button" type="button" disabled={uploading || chat.files.length >= 10} aria-label="Прикрепить файл" title="Прикрепить файл" onClick={() => fileInput.current?.click()}><Paperclip /></button>
          <button className={`icon-button button-ghost voice-button${listening ? " listening" : ""}${transcribing ? " transcribing" : ""}`} type="button" disabled={busy || transcribing} aria-label={transcribing ? "Распознаётся голос" : listening ? "Остановить запись" : "Голосовой ввод"} aria-pressed={listening} title={transcribing ? "Распознаётся голос" : listening ? "Остановить запись" : "Голосовой ввод"} onClick={() => void toggleVoiceInput()}>
            {transcribing ? <LoaderCircle className="spin" /> : listening ? <Square className="recording-stop-icon" /> : <Mic />}
          </button>
          <textarea rows={1} placeholder="Сообщение SuperGPT" value={draft} onChange={(event) => setDraft(event.target.value)} onKeyDown={keyDown} disabled={busy || transcribing} />
          <button className="send-button" type="button" disabled={busy || listening || transcribing || !draft.trim()} aria-label={busy ? "Ответ создаётся" : "Отправить"} title="Отправить" onClick={() => void send()}>
            {busy ? <Square /> : <SendHorizontal />}
          </button>
        </div>
      </footer>
    </section>
  );
}
